import createStore from 'magic-tricks/lib/createStore'
import cookie from 'js-cookie'
import jwt_decode from 'jwt-decode'

import logout from '../services/account/logout'
import getToken from '../services/account/getToken'
import acceptTracking from '../services/account/acceptTracking'

import hydrateCharts from '../services/charts/hydrateCharts'

import hydrateFundStats from '../services/fundStats/hydrateFundStats'

import hydrateEvents from '../services/events/hydrateEvents'
import hydrateWorkingGroupsEvents from '../services/events/hydrateWorkingGroupsEvents'
import hydrateUpcomingWorkingGroupsEvents from '../services/events/hydrateUpcomingWorkingGroupsEvents'
import hydrateOrganizers from '../services/events/hydrateOrganizers'
import createEvent from '../services/events/createEvent'
import editEvent from '../services/events/editEvent'
import deleteEvent from '../services/events/deleteEvent'

import hydrateDocuments from '../services/documents/hydrateDocuments'
import uploadFile from '../services/documents/uploadFile'
import createEipDocument from '../services/documents/createEipDocument'
import deleteDocument from '../services/documents/deleteDocument'
import updateDocumentsQuery from '../services/documents/updateDocumentsQuery'

import hydrateFundNews from '../services/fundNews/getFundNews'
import createFundNews from '../services/fundNews/createFundNews'
import editFundNews from '../services/fundNews/editFundNews'
import deleteFundNews from '../services/fundNews/deleteFundNews'

import hydrateCustomerNews from '../services/customerNews/hydrateCustomerNews'
import createCustomerNews from '../services/customerNews/createCustomerNews'
import editCustomerNews from '../services/customerNews/editCustomerNews'
import deleteCustomerNews from '../services/customerNews/deleteCustomerNews'

import hydrateSector from '../services/sectors/hydrateSector'
import hydrateSectors from '../services/sectors/hydrateSectors'

import hydrateCompanies from '../services/companies/hydrateCompanies'
import hydrateCompany from '../services/companies/hydrateCompany'
import createCompany from '../services/companies/createCompany'
import updateCompaniesQuery from '../services/companies/updateCompaniesQuery'
import deleteCompany from '../services/companies/deleteCompany'

export const PASSWORD_KEY = 'eip_password'
export const TOKEN_KEY = 'eip_token'

export const DEFAULT_STATE = {
  hasEnteredSitePassword: false,

  // Account
  token: null,
  accessToken: null,
  user: null,
  doNotTrack: false,
  acceptsTracking: false,
  isAdmin: false,

  // Charts
  charts: [],
  isHydratingCharts: false,

  // Fund Stats
  fundStats: [],
  isHydratingFundStats: false,

  // Events
  events: [],
  organizers: [],
  isHydratingEvents: false,

  // Working Groups
  workingGroupsEvents: [],
  isHydratingWorkingGroupsEvents: false,
  upcomingWorkingGroupsEvents: [],
  isHydratingUpcomingWorkingGroupsEvents: false,

  // Events
  customerNews: [],
  isHydratingCustomerNews: false,

  // Sectors
  sectors: [],
  isHydratingSectors: false, // sector list
  isHydratingSector: false, // sector detail modal

  // Companies
  isHydratingCompanies: false, // company list
  isHydratingCompany: false, // company detail modal
  companies: [],
  companyListPage: 1,
  totalCompanies: 0,
  companyListSortBy: 'name',
  companyListSortDirection: 'ascend',
  companyListSearch: null,

  // Documents
  documents: [],
  isHydratingDocuments: false,
  documentCenterPage: 1,
  totalDocuments: 0,
  documentCenterSortBy: '_createdAt',
  documentCenterSortDirection: 'descend',
  documentCenterSearch: null,
  documentCenterFilters: {
    audience: null,
    keywords: null,
    type: null,
  },

  // Dashboard
  isHydratingFundNews: false,
  fundNews: [],
}

//
// === Actions ===
//

const completePasswordCheck = store => () => {
  cookie.set(PASSWORD_KEY, 'true', {expires: 7})

  store.setState({hasEnteredSitePassword: true})
}

const hydratePassword = store => () => {
  const hasEnteredSitePassword = Boolean(cookie.get(PASSWORD_KEY))

  store.setState({hasEnteredSitePassword})
}

const hydrateTokenFromCache = store => () => {
  const token = cookie.get(TOKEN_KEY)

  if (!token) return

  const {user, accessToken, trackingConfig} = jwt_decode(token)
  const {doNotTrack, acceptsTracking} = trackingConfig || {}
  const isAdmin = user.role === 'admin' || user.role === 'coadmin'

  store.setState({
    token,
    doNotTrack,
    acceptsTracking,
    accessToken,
    user,
    isAdmin,
  })
}

export const hydrateTokenFromServer = store => ({token}) => {
  cookie.set(TOKEN_KEY, token, {expires: 25})
  const {user, accessToken, trackingConfig} = jwt_decode(token)
  const {doNotTrack, acceptsTracking} = trackingConfig || {}
  const isAdmin = user.role === 'admin' || user.role === 'coadmin'

  store.setState({
    token,
    accessToken,
    user,
    doNotTrack,
    acceptsTracking,
    isAdmin,
  })
}

//
// === Create Store ===
//

export default (initialState = {}) => {
  //
  // === Store ===
  //

  const store = createStore({
    ...DEFAULT_STATE,
    ...initialState,
  })

  //
  // === Interface ===
  //

  return {
    // Interface
    getState: store.getState,
    setState: store.setState,
    listen: store.listen,
    unlisten: store.unlisten,

    // Actions
    completePasswordCheck: completePasswordCheck(store),
    hydratePassword: hydratePassword(store),
    hydrateTokenFromCache: hydrateTokenFromCache(store),
    hydrateTokenFromServer: hydrateTokenFromServer(store),
    logout: logout(store),
    getToken: getToken(store),
    acceptTracking: acceptTracking(store),

    hydrateCharts: hydrateCharts(store),

    hydrateFundStats: hydrateFundStats(store),

    hydrateEvents: hydrateEvents(store),
    hydrateWorkingGroupsEvents: hydrateWorkingGroupsEvents(store),
    hydrateUpcomingWorkingGroupsEvents: hydrateUpcomingWorkingGroupsEvents(
      store,
    ),
    hydrateOrganizers: hydrateOrganizers(store),
    createEvent: createEvent(store),
    editEvent: editEvent(store),
    deleteEvent: deleteEvent(store),

    hydrateDocuments: hydrateDocuments(store),
    updateDocumentsQuery: updateDocumentsQuery(store),
    createEipDocument: createEipDocument(store),
    deleteDocument: deleteDocument(store),
    uploadFile: uploadFile(store),

    hydrateFundNews: hydrateFundNews(store),
    createFundNews: createFundNews(store),
    editFundNews: editFundNews(store),
    deleteFundNews: deleteFundNews(store),

    hydrateCustomerNews: hydrateCustomerNews(store),
    createCustomerNews: createCustomerNews(store),
    editCustomerNews: editCustomerNews(store),
    deleteCustomerNews: deleteCustomerNews(store),

    hydrateSector: hydrateSector(store),
    hydrateSectors: hydrateSectors(store),

    hydrateCompany: hydrateCompany(store),
    hydrateCompanies: hydrateCompanies(store),
    updateCompaniesQuery: updateCompaniesQuery(store),
    createCompany: createCompany(store),
    deleteCompany: deleteCompany(store),
  }
}
